import React, { FC, memo, useContext } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { IHttpRequestConfig } from "@/browser";
import { IFetcherRenderProps } from "@/components/Fetcher";
import { LoadingSpinner } from "@/components/Loading";
import { Section } from "@/components/Section";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { fetchWorkOrder, IWorkOrder, IWorkOrderDetailRouteProps } from "@/data/work-orders";
import { Row } from "@/eworkitComponents";
import { useFetcher } from "@/hooks/http";
import { usePreviousRoutePath, withRouteProps } from "@/infrastructure/Route";
import { BackButton } from "@/views/components/BackButton";
import { Container } from "@/views/components/Container";
import { withHttpError } from "@/views/Errors";
import { JobRequestDetailBackground } from "@/views/JobRequests/Detail/components";
import { router } from "@/views/router";

import { transformRouteProps } from "../selectors";
import { WorkOrderDetails } from "./WorkOrderDetails";

interface IWorkOrderFetcherProps extends IWorkOrderDetailRouteProps {}

interface IWorkOrderDetailRendererProps extends IFetcherRenderProps<IWorkOrder> {}

export const WorkOrderDetailsPageRendererComponent: FC<IWorkOrderDetailRendererProps> = props => {
  const { previousPath } = usePreviousRoutePath();
  const { t } = useTranslation(["public", "session"]);
  const { payload, fetch } = props;
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  return (
    <>
      <Helmet>
        <title>{t("META.WORK_ORDER_DETAIL.TITLE")}</title>
        <meta name="description" content={t("META.WORK_ORDER_DETAIL.DESCRIPTION")} />
      </Helmet>

      <JobRequestDetailBackground />

      <Container padding={isSmallScreen ? ["md", false] : ["md"]}>
        <Row
          size="md"
          direction="row"
          padding={isSmallScreen ? [false, "md", "md", "md"] : [false, false, "md", false]}>
          <BackButton
            path={previousPath ?? router.get("assignments").path}
            label={t("session:WORK_ORDERS.DETAILS.BACK_TO_WORK_ORDERS")}
          />
        </Row>

        <Row>
          {payload ? (
            <WorkOrderDetails workOrder={payload} update={fetch} />
          ) : (
            <Section
              flex
              grow={1}
              align="center"
              justify="center"
              shadow={isSmallScreen ? "none" : "default"}
              borderRadius={!isSmallScreen}
              padding={["xxl"]}
              margin={[false, false, "md", false]}>
              <LoadingSpinner size="lg" variant="primary" />
            </Section>
          )}
        </Row>
      </Container>
    </>
  );
};

const WorkOrderDetailsPageRenderer = withHttpError(WorkOrderDetailsPageRendererComponent);

export const WorkOrderDetailsPageComponent: FC<IWorkOrderFetcherProps> = memo(({ routeProps }) => {
  const { contextCompany } = useContext(UserContext);

  const fetcher = useFetcher(
    (_: any, httpConfig: IHttpRequestConfig) => {
      return fetchWorkOrder({ workOrderId: routeProps.workOrderId, companyId: contextCompany?.id }, httpConfig);
    },
    {},
    [routeProps.workOrderId, contextCompany?.id],
  );

  return <WorkOrderDetailsPageRenderer {...fetcher} />;
});

export const WorkOrderDetailsPage = withRouteProps(WorkOrderDetailsPageComponent, transformRouteProps);
